import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Stack, styled } from '@mui/material';
import { TextInput } from '../../components';
import { CardPaymentResultPopup } from '../../components/CardPaymentResultPopup';
import { useLoading } from '../../context';
import { useLocation } from "react-router-dom";

const CHALLENGE_POLL_INTERVAL_MS = 500;
const CHALLENGE_TIMEOUT_MS = 10 * 60 * 1000;
const METHOD_POLL_INTERVAL_MS = 500;
const METHOD_TIMEOUT_MS = 10 * 1000;

const ThreeDSecureIframeWrapper = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  background: 'white',
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[5],
  zIndex: 1300,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const ThreeDSecureIframe = styled('iframe')({
  width: '600px',
  height: '500px',
  border: 'none',
});

const parseExpirationDate = (expirationYearMonth) => {
  if (!expirationYearMonth) return { month: '', year: '' };
  const [month, year] = expirationYearMonth.split('/').map((v) => v.trim());
  if (!month || !year) return { month: '', year: '' };
  const currentCentury = Math.floor(new Date().getFullYear() / 100) * 100;
  const normalizedYear = year.length === 2 ? currentCentury + Number(year) : Number(year);
  return { month: month.padStart(2, '0'), year: String(normalizedYear) };
};

const injectMethodForm = (html) => {
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'visibility: hidden; position: absolute; width: 1px; height: 1px; left: -9999px; top: -9999px; border: none;';
  iframe.srcdoc = html;
  document.body.appendChild(iframe);

  return () => {
    if (document.body.contains(iframe)) document.body.removeChild(iframe);
  };
};

const buildChallengeSrcdoc = ({ acsURL, cReq, sessionData }) => `
<!DOCTYPE html><html><body>
  <form method="POST" action="${acsURL}">
    <input type="hidden" name="creq" value="${cReq}">
    ${sessionData ? `<input type="hidden" name="threeDSSessionData" value="${sessionData}">` : ''}
  </form>
  <script>document.forms[0].submit();<\/script>
</body></html>`;

const pollWithTimeout = async (pollFn, intervalMs, timeoutMs) => {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const result = await pollFn();
    if (result !== null) return result;
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return null;
};

const FiservIpgCardPayment = forwardRef(({ billAmount, billReference, userEmail, creditCardImage, customerName = '', contactPhone = '' }, ref) => {
  const [cardholderName, setCardholderName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [challengeParams, setChallengeParams] = useState(null);
  const [flashKey, setFlashKey] = useState(0);
  const [flashStatus, setFlashStatus] = useState(null);
  const { showLoading, hideLoading } = useLoading();

  const searchParams = new URLSearchParams(useLocation().search);
  const env = searchParams.get('env');

  useImperativeHandle(ref, () => ({
    paymentSubmit: (setPaymentStatusCallback) =>
      handlePaymentSubmit(setPaymentStatusCallback),
  }));

  const handleExpirationChange = (value) => {
    let cleaned = value.replace(/[^\d/]/g, '');
    if (/^\d{3,4}$/.test(cleaned)) {
      cleaned = cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4);
    } else if (/^\d{2}\/?\d{0,2}$/.test(cleaned)) {
      if (cleaned.length > 2 && cleaned[2] !== '/') {
        cleaned = cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4);
      }
    }
    if (cleaned.length > 5) cleaned = cleaned.slice(0, 5);
    setExpiryDate(cleaned);
  };

  const handleCardNumberChange = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 16);
    setCardNumber(digits.replace(/(.{4})/g, '$1 ').trim());
  };

  const showResult = (status, setPaymentStatusCallback) => {
    hideLoading();
    setChallengeParams(null);
    setFlashStatus(status);
    setFlashKey((k) => k + 1);
    setPaymentStatusCallback(status === 'PAY_SUCCESS' ? 'Payment Successful.' : 'Payment Failed.');
  };

  const showError = (message, setPaymentStatusCallback) => {
    hideLoading();
    setChallengeParams(null);
    setPaymentStatusCallback(message || 'Payment Failed.');
    setFlashStatus('PAY_ERROR');
    setFlashKey((k) => k + 1);
  };

  const pollMethodNotification = async (transactionId) => {
    const res = await fetch(`/api/fiserv-ipg/poll-method-notification?transactionId=${transactionId}&env=${env}`);
    if (!res.ok) return false;
    const data = await res.json();
    return data.notified ? true : null;
  };

  const pollCres = async (transactionId) => {
    const res = await fetch(`/api/fiserv-ipg/poll-cres?transactionId=${transactionId}&env=${env}`);
    if (!res.ok) return null;
    const data = await res.json();
    return data.cRes || null;
  };

  const createPayment = async ({ expiryMonth, expiryYear }) => {
    const res = await fetch(`/api/fiserv-ipg/create-payment?env=${env}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        cardNumber: cardNumber.replace(/\s/g, ''),
        securityCode: cvv,
        expiryMonth,
        expiryYear,
        cardholderName,
        billAmount,
        billReference,
        userEmail,
        customerName,
        contactPhone,
        xeroParams: {
          XeroAmount: searchParams.get('XeroAmount') || null, // Optional: XeroAmount from query params, may different from billAmount if user modified it.
          XeroInvoiceNo: searchParams.get('XeroInvoiceNo') || null,
          XeroShortCode: searchParams.get('XeroShortCode') || null,
        }
      }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to create payment');
    }
    return res.json();
  };

  const updateAuthentication = async (payload) => {
    const res = await fetch(`/api/fiserv-ipg/update-authentication?env=${env}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to update authentication');
    }
    return res.json();
  };

  const handlePaymentSubmit = async (setPaymentStatusCallback) => {
    if (!cardholderName || !cardNumber || !expiryDate || !cvv) {
      setPaymentStatusCallback('Please fill in all card details.');
      return;
    }

    const { month: expiryMonth, year: expiryYear } = parseExpirationDate(expiryDate);
    if (!expiryMonth || !expiryYear) {
      setPaymentStatusCallback('Invalid expiry date.');
      return;
    }

    showLoading();

    try {
      // Step 1: Initial payment request
      const createResult = await createPayment({ expiryMonth, expiryYear });
      const { transactionId, ipgTransactionId, transactionStatus, methodForm } = createResult;

      // Early Exit A: card not enrolled / 3DS unavailable
      if (transactionStatus === 'APPROVED') {
        showResult('PAY_SUCCESS', setPaymentStatusCallback);
        return;
      }
      if (transactionStatus === 'DECLINED') {
        showResult('PAY_ERROR', setPaymentStatusCallback);
        return;
      }

      if (transactionStatus !== 'WAITING') {
        showError(`Unexpected transaction status: ${transactionStatus}`, setPaymentStatusCallback);
        return;
      }

      // Step 2: 3DS method collection
      let methodNotificationStatus = 'EXPECTED_BUT_NOT_RECEIVED';
      let cleanupMethodForm = null;

      if (methodForm) {
        cleanupMethodForm = injectMethodForm(methodForm);
        const notified = await pollWithTimeout(
          () => pollMethodNotification(transactionId),
          METHOD_POLL_INTERVAL_MS,
          METHOD_TIMEOUT_MS
        );
        methodNotificationStatus = notified ? 'RECEIVED' : 'EXPECTED_BUT_NOT_RECEIVED';
        cleanupMethodForm();
      }

      // Step 3: First PATCH — notify gateway of method result
      const patchResult = await updateAuthentication({
        transactionId,
        ipgTransactionId,
        methodNotificationStatus,
      });

      // Frictionless flow
      if (patchResult.transactionStatus === 'APPROVED') {
        showResult('PAY_SUCCESS', setPaymentStatusCallback);
        return;
      }
      if (patchResult.transactionStatus === 'DECLINED') {
        showResult('PAY_ERROR', setPaymentStatusCallback);
        return;
      }

      // Challenge flow
      if (patchResult.transactionStatus === 'WAITING' && patchResult.challengeParams?.acsURL) {
        setChallengeParams(patchResult.challengeParams);
        hideLoading();

        const cRes = await pollWithTimeout(
          () => pollCres(transactionId),
          CHALLENGE_POLL_INTERVAL_MS,
          CHALLENGE_TIMEOUT_MS
        );

        if (!cRes) {
          showError('Challenge timed out, please try again.', setPaymentStatusCallback);
          return;
        }

        showLoading();
        setChallengeParams(null);

        // Step 4: Final PATCH with cRes
        const finalResult = await updateAuthentication({
          transactionId,
          ipgTransactionId,
          cRes,
        });

        if (finalResult.transactionStatus === 'APPROVED') {
          showResult('PAY_SUCCESS', setPaymentStatusCallback);
        } else {
          showResult('PAY_ERROR', setPaymentStatusCallback);
        }
        return;
      }

      // Unexpected WAITING state with no challenge
      showError('Unexpected authentication state, please try again.', setPaymentStatusCallback);
    } catch (err) {
      console.error('[FiservIpgCardPayment] payment error', err);
      showError(err.message || 'Payment failed, please try again.', setPaymentStatusCallback);
    }
  };

  return (
    <Stack gap="10px" width="100%">
      <div style={{ position: 'relative', marginTop: '30px' }}>
        <label style={{ position: 'absolute', left: 5, top: '-28px', fontSize: '13px', fontWeight: '600' }}>
          Cardholder name
        </label>
        <TextInput placeholder="John Smith" value={cardholderName} onChange={setCardholderName} />
      </div>

      <div style={{ width: '100%', position: 'relative', marginTop: '30px' }}>
        <label style={{ position: 'absolute', left: 5, top: '-28px', fontSize: '13px', fontWeight: '600' }}>
          Card number
        </label>
        <TextInput
          placeholder="1234 1234 1234 1234"
          value={cardNumber}
          onChange={handleCardNumberChange}
        />
        {creditCardImage && (
          <img
            style={{ position: 'absolute', right: '10px', bottom: '8px', height: '29px' }}
            src={creditCardImage}
            alt="card"
          />
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', marginTop: '30px' }}>
        <div style={{ display: 'inline-block', width: '49%', position: 'relative' }}>
          <label style={{ position: 'absolute', left: 5, top: '-28px', fontSize: '13px', fontWeight: '600' }}>
            Expiration date
          </label>
          <TextInput placeholder="MM/YY" value={expiryDate} onChange={handleExpirationChange} />
        </div>
        <div style={{ display: 'inline-block', width: '49%', marginLeft: 'auto', position: 'relative' }}>
          <label style={{ position: 'absolute', left: 5, top: '-28px', fontSize: '13px', fontWeight: '600' }}>
            Security code
          </label>
          <TextInput placeholder="CVV" value={cvv} onChange={setCvv} />
        </div>
      </div>

      <CardPaymentResultPopup key={flashKey} paymentStatus={flashStatus} />

      {challengeParams && (
        <ThreeDSecureIframeWrapper>
          <ThreeDSecureIframe
            title="3D Secure Challenge"
            srcDoc={buildChallengeSrcdoc(challengeParams)}
          />
        </ThreeDSecureIframeWrapper>
      )}
    </Stack>
  );
});

export { FiservIpgCardPayment };
